import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import ModalConfirmationContent from '../../../../shared/components/modal-confirmation-content/modal-confirmation-content';
import Modal from '../../../../shared/components/modal/modal';
import { useConfirmationError } from '../../../../shared/hooks/use-confirmation-error';
import { useAppSelector } from '../../../../store/hooks';
import { requestPreviewAlertId } from '../../../request/constants/constants';
import LazyModal, { LazyModalComponentProps } from '../../../ui/components/lazy-modal';
import { closeModal, setAlert } from '../../../ui/store/ui-slice';
import { useRestoreScanRequestMutation } from '../../api/scan-requests-api';
import { scanRequestAlertId, scanRequestRestoreModalId } from '../../constants/constants';
import { selectTempScanRequestId, setTempScanRequestId } from '../../store/scan-requests-slice';

const ScanRequestRestoreModalContent = ({ isOpen, afterClose }: LazyModalComponentProps): JSX.Element => {
  const { t } = useTranslation(['scan-requests', 'common']);
  const dispatch = useDispatch();
  const activeScanRequestId = useAppSelector(selectTempScanRequestId);
  const { confirmationError, addConfirmationError, resetConfirmationError } = useConfirmationError();

  const [restoreScanRequest, { isLoading }] = useRestoreScanRequestMutation();

  const closeFormModal = () => {
    dispatch(closeModal(scanRequestRestoreModalId));
    dispatch(setTempScanRequestId(null));
  };

  const setScanRequestRestoreAlertMessage = (alertId: string) => {
    dispatch(
      setAlert({
        id: alertId,
        type: 'success',
        title: t('scanRequestRestoreModal.message.fileRestored'),
      }),
    );
  };

  const onModalSubmit = () => {
    if (!activeScanRequestId) {
      addConfirmationError(t('signScanRequest.validationError.restoreFailed', { ns: 'common' }));
      return;
    }
    resetConfirmationError();

    restoreScanRequest(activeScanRequestId)
      .unwrap()
      .then(() => {
        closeFormModal();
        setScanRequestRestoreAlertMessage(scanRequestAlertId);
        setScanRequestRestoreAlertMessage(requestPreviewAlertId);
      })
      .catch(() => {
        addConfirmationError(t('signScanRequest.validationError.restoreFailed', { ns: 'common' }));
      });
  };

  return (
    <Modal
      title={t('scanRequestRestoreModal.title')}
      onCancel={closeFormModal}
      isLoading={isLoading}
      customSubmitText={t('signScanRequest.action.restore', { ns: 'common' })}
      onOk={onModalSubmit}
      isOpen={isOpen}
      afterClose={afterClose}
    >
      <ModalConfirmationContent
        confirmDeleteText={t('signScanRequest.hint.restoreWarning', { ns: 'common' })}
        confirmationError={confirmationError}
      />
    </Modal>
  );
};

const ScanRequestRestoreModal = (): JSX.Element => {
  return <LazyModal modalId={scanRequestRestoreModalId} Component={ScanRequestRestoreModalContent} />;
};

export default ScanRequestRestoreModal;
